import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Globe, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useDetectedCountry } from './ShipToCountryBanner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'rm_currency';

const CurrencyContext = React.createContext(null);

const LOCALE_BY_LANG = {
  fr: 'fr-FR', en: 'en-US', de: 'de-DE', it: 'it-IT', es: 'es-ES', nl: 'nl-NL', pl: 'pl-PL',
  pt: 'pt-PT', zh: 'zh-CN', tr: 'tr-TR', cs: 'cs-CZ', sv: 'sv-SE', ru: 'ru-RU', ja: 'ja-JP', ar: 'ar',
};

export const useCurrency = () => {
  const c = React.useContext(CurrencyContext);
  if (!c) return { code: 'EUR', symbol: '€', rate: 1.0, setCode: () => {}, format: (v) => `€${(v || 0).toFixed(2)}` };
  return c;
};

export const CurrencyProvider = ({ children }) => {
  const { language } = useLanguage();
  const [code, setCodeState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'EUR');
  const [currencies, setCurrencies] = useState([]);
  const detectedCountry = useDetectedCountry();

  useEffect(() => {
    axios.get(`${API}/currencies`).then((r) => setCurrencies(r.data?.currencies || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!detectedCountry || localStorage.getItem(STORAGE_KEY)) return;
    const cc = (detectedCountry.match(/^[A-Za-z]{2}/) || [])[0];
    if (!cc) return;
    axios.get(`${API}/currencies/auto`, { params: { country: cc.toUpperCase() } })
      .then((r) => {
        const detected = r.data?.currency;
        if (detected) {
          setCodeState(detected);
          localStorage.setItem(STORAGE_KEY, detected);
        }
      })
      .catch(() => {});
  }, [detectedCountry]);

  const setCode = (c) => {
    setCodeState(c);
    localStorage.setItem(STORAGE_KEY, c);
    window.dispatchEvent(new CustomEvent('currency-changed', { detail: c }));
  };

  const current = currencies.find((c) => c.code === code) || { code: 'EUR', symbol: '€', rate_from_eur: 1.0 };

  return (
    <CurrencyContext.Provider value={{
      code,
      symbol: current.symbol,
      rate: current.rate_from_eur,
      setCode,
      currencies,
      format: (amount_eur) => {
        let v = (amount_eur || 0) * (current.rate_from_eur || 1);
        if (code !== 'EUR' && v > 0) {
          if (code === 'JPY') v = Math.round(v);
          else if (v >= 1) v = Math.round(v) - 0.01;
        }
        try {
          return new Intl.NumberFormat(LOCALE_BY_LANG[language] || 'en-US', {
            style: 'currency', currency: code, maximumFractionDigits: 2,
          }).format(v);
        } catch {
          return `${v.toFixed(2)} ${current.symbol}`;
        }
      },
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const CurrencySelector = () => {
  const { code, setCode, currencies } = useCurrency();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm font-semibold text-[#0F4C5C]/80 hover:text-[#0F4C5C] px-2 py-1.5 rounded-lg hover:bg-[#0F4C5C]/5 transition-colors"
          data-testid="currency-selector"
        >
          <Globe className="w-4 h-4" />
          <span>{code}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {currencies.map((c) => (
          <DropdownMenuItem
            key={c.code}
            onClick={() => setCode(c.code)}
            className="flex items-center justify-between cursor-pointer"
            data-testid={`currency-${c.code}`}
          >
            <span className="flex items-center gap-2">
              <span className="font-mono text-xs text-[#0F4C5C]/60 w-8">{c.code}</span>
              <span className="text-sm">{c.name}</span>
            </span>
            {code === c.code && <Check className="w-4 h-4 text-[#E9C46A]" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
